import React, { useEffect, useMemo, useRef, useState } from "react";
import { DatePicker } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";

import { CustomDropdown } from "../../components/visuals/PageFilter/PageFilter";
import { ElementContainer } from "../../components/containers/styledComponents";
import { usePost } from "../../hooks/usePost";
import { EngagementType } from "../../models/stewardshipModels";
import { useToast } from "../../store/ToastProvider";
import { ManagerItem } from "../../types/CommonDataRequestTypes";
import {
  DashboardFilters,
  ManagerId,
  ThemeId,
} from "../../types/engagementAssessmentV2Types";
import { Filter as Option } from "../../types/PageFilterTypes";
import { DEFAULT_DATE_RANGE_DAYS } from "./constants";

const MANAGER_FILTER_PLACEHOLDER: Option = {
  key: null,
  displayValue: "Select manager",
};

const THEME_FILTER_PLACEHOLDER: Option = {
  key: null,
  displayValue: "Select theme",
};

const THEME_OPTIONS: Option[] = Object.values(EngagementType).map((theme) => ({
  key: theme,
  displayValue: theme,
}));

export const buildInitialDashboardFilters = (): DashboardFilters => {
  const end = dayjs().startOf("day");
  const start = end.subtract(DEFAULT_DATE_RANGE_DAYS, "day");
  return {
    managerIds: [],
    themes: [],
    dateRange: {
      start: start.format("YYYY-MM-DD"),
      end: end.format("YYYY-MM-DD"),
    },
  };
};

export const DashboardFilterBar = ({
  value,
  onChange,
}: DashboardFilterBarProps): JSX.Element => {
  const { addToast } = useToast();

  const { sendData: fetchManagers, isLoading: isManagersLoading } = usePost<
    ManagerItem[],
    null
  >();

  const [managerOptions, setManagerOptions] = useState<Option[]>([]);

  const hasFetchedManagersRef = useRef<boolean>(false);
  useEffect(() => {
    if (hasFetchedManagersRef.current) return;
    hasFetchedManagersRef.current = true;

    let cancelled = false;
    const loadManagers = async (): Promise<void> => {
      try {
        const data = await fetchManagers("GET_ALL_MANAGERS", null);
        if (cancelled || !data) return;
        setManagerOptions(
          data.map((m) => ({
            key: m.manager_id,
            displayValue: m.manager_name,
          })),
        );
      } catch {
        if (!cancelled) addToast("Failed to load managers", "error");
      }
    };

    void loadManagers();
    return (): void => {
      cancelled = true;
    };
  }, [fetchManagers, addToast]);

  const rangeValue = useMemo<[Dayjs, Dayjs]>(
    () => [dayjs(value.dateRange.start), dayjs(value.dateRange.end)],
    [value.dateRange.start, value.dateRange.end],
  );

  const handleManagerChange = (options: Option[]): void => {
    const managerIds: ManagerId[] = options
      .map((o) => o.key)
      .filter((k): k is string => k !== null);
    onChange({ ...value, managerIds });
  };

  const handleThemeChange = (options: Option[]): void => {
    const themes: ThemeId[] = options
      .map((o) => o.key)
      .filter((k): k is string => k !== null);
    onChange({ ...value, themes });
  };

  const handleDateChange = (
    range: [Dayjs | null, Dayjs | null] | null,
  ): void => {
    if (!range || !range[0] || !range[1]) return;
    onChange({
      ...value,
      dateRange: {
        start: range[0].format("YYYY-MM-DD"),
        end: range[1].format("YYYY-MM-DD"),
      },
    });
  };

  return (
    <ElementContainer ai="center" flexDirection="row" jc="flex-start">
      <CustomDropdown
        options={managerOptions}
        isLoading={isManagersLoading}
        placeholder={MANAGER_FILTER_PLACEHOLDER}
        isMultiSelect
        onSelectOptions={handleManagerChange}
      />
      <CustomDropdown
        options={THEME_OPTIONS}
        placeholder={THEME_FILTER_PLACEHOLDER}
        isMultiSelect
        onSelectOptions={handleThemeChange}
      />
      <DatePicker.RangePicker
        value={rangeValue}
        onChange={handleDateChange}
        allowClear={false}
        format="YYYY-MM-DD"
      />
    </ElementContainer>
  );
};

type DashboardFilterBarProps = {
  value: DashboardFilters;
  onChange: (filters: DashboardFilters) => void;
};
